/* eslint max-lines: ["error", {max: 367}] -- Due to the nature of the component */
/* eslint max-statements: ["error", {max: 33}] -- Due to the nature of the component */
/* eslint max-lines-per-function: ["error", {max: 321}] -- Due to the nature of the component */
import { useEffect, useRef, useState } from "react";

import isEmpty from "lodash/isEmpty";
import { CredentialsManagerErrorCodes } from "react-native-auth0";
import type { WebViewNavigation } from "react-native-webview";

import { AnalyticsFieldName } from "@/constants/analytics/Analytics";
import {
  LoginAnalyticsEvent,
  LoginAnalyticsPageView,
} from "@/constants/analytics/Login";
import { AuthErrorCode } from "@/constants/Auth0Errors";
import type { SerializableAuthError } from "@/constants/Auth0Errors";
import { LoginTestID } from "@/constants/TestID";
import useAuth from "@/hooks/useAuth";
import useLocalAuthentication from "@/hooks/useLocalAuthentication";
import { SecureStorageKey, useSecureStorage } from "@/hooks/useSecureStorage";
import { useToast } from "@/hooks/useToast";
import {
  UnencryptedStorageKey,
  useUnencryptedStorage,
} from "@/hooks/useUnencryptedStorage";
import { useAnalytics } from "@/services/Analytics";
import { useAppStartupDispatch } from "@/store/reducers/appStartup";
import { useLoginDispatch, useLoginSelector } from "@/store/reducers/login";
import WebViewModal from "@/ui/composed/media/webView/WebViewModal";
import { Button, FAIconName, TextField } from "@/ui/core";
import LabelWithSwitch from "@/ui/core/LabelWithSwitch";
import { KeyboardAvoidingView, VStack } from "@/ui/layout";
import { ColorToken } from "@/ui/theme/tokens";
import { getBiometricsLabel } from "@/utils/auth.util";
import { toMs } from "@/utils/date.util";
import { joinTestIDs } from "@/utils/string.util";

const BlockDurationMs = toMs({ minutes: 2 });

interface LoginFormProps {
  isLoginDisabled: boolean;
}

const LoginForm = ({ isLoginDisabled }: LoginFormProps) => {
  const {
    clearCredentials,
    isLoading,
    loginWithPasswordRealm,
    passwordResetUrl,
  } = useAuth();
  const { setCanLoggedInUserSeeAppContent } = useAppStartupDispatch();

  const {
    authenticateAsync,
    isBiometricAuthAvailable,
    isFaceRecognitionSupported,
    setIsLocalAuthenticationEnabled,
  } = useLocalAuthentication();
  const { track } = useAnalytics();
  const { showToast } = useToast();
  const { getSecureStringValue, saveSecureStringValue } = useSecureStorage();
  const [password, setPassword] = useState<string>("");

  const { getUnencryptedBooleanValue, saveUnencryptedBooleanValue } =
    useUnencryptedStorage();

  const { setEmail: setPersistedEmail } = useLoginDispatch();
  const { email: persistedEmail } = useLoginSelector();
  const [email, setEmail] = useState<string>(persistedEmail);

  const [isWebviewOpen, setIsWebviewOpen] = useState<boolean>(false);

  const [shouldRememberEmail, setShouldRememberEmail] =
    useState<boolean>(false);

  const [shouldEnableBiometrics, setShouldEnableBiometrics] =
    useState<boolean>(false);

  const [isUserBlocked, setIsUserBlocked] = useState<boolean>(false);
  const blockTimeoutRef = useRef<null | ReturnType<typeof setTimeout>>(null);
  const retryCountRef = useRef<number>(0);

  useEffect(() => {
    return () => {
      if (blockTimeoutRef.current) {
        clearTimeout(blockTimeoutRef.current);
      }
    };
  }, []);

  const startBlockTimer = () => {
    if (blockTimeoutRef.current) {
      clearTimeout(blockTimeoutRef.current);
    }
    blockTimeoutRef.current = setTimeout(() => {
      setIsUserBlocked(false);
      blockTimeoutRef.current = null;
    }, BlockDurationMs);
  };

  const isSignInDisabled =
    isEmpty(email) || isEmpty(password) || isLoginDisabled || isUserBlocked;

  useEffect(() => {
    const getLoginPreferencesAsync = async () => {
      const isRememberMeChecked = await getUnencryptedBooleanValue(
        UnencryptedStorageKey.IsRememberMeEnabled,
      );
      const isBiometricAuthEnabled = await getUnencryptedBooleanValue(
        UnencryptedStorageKey.IsBiometricAuthEnabled,
      );

      const storedEmail = await getSecureStringValue(SecureStorageKey.Email);

      const shouldPrefillEmail = isRememberMeChecked;

      setShouldRememberEmail(isRememberMeChecked);
      setShouldEnableBiometrics(isBiometricAuthEnabled);

      if (storedEmail && shouldPrefillEmail) {
        setEmail(storedEmail);
      }
    };

    void getLoginPreferencesAsync();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only want to run on mount
  }, []);

  useEffect(() => {
    if (passwordResetUrl) {
      setIsWebviewOpen(true);
    }
  }, [passwordResetUrl]);

  const saveEmail = async () => {
    await saveUnencryptedBooleanValue({
      isTrue: true,
      key: UnencryptedStorageKey.IsRememberMeEnabled,
    });

    await saveSecureStringValue({ key: SecureStorageKey.Email, value: email });
  };

  const saveCredentials = async () => {
    await saveUnencryptedBooleanValue({
      isTrue: true,
      key: UnencryptedStorageKey.IsBiometricAuthEnabled,
    });

    await saveSecureStringValue({ key: SecureStorageKey.Email, value: email });
    await saveSecureStringValue({
      key: SecureStorageKey.Password,
      value: password,
    });
  };

  const handleAuthError = async (authError: SerializableAuthError) => {
    const isEncryptionError =
      authError.type === CredentialsManagerErrorCodes.CRYPTO_EXCEPTION;
    // If we get the encryption error the first time we want to sign in automatically,
    // but if we get the same error again we want to show the toast error as well
    if (
      // We only want to handle the reattempt one time so if it is attempted before we dont want to attempt again
      isEncryptionError &&
      retryCountRef.current === 0
    ) {
      retryCountRef.current += 1;
      await authorizeWithPasswordRealm();
      return;
    }

    // Check if this is a user blocked error
    const isUserBlockedError =
      authError.code === AuthErrorCode.TooManyAttempts ||
      authError.code === AuthErrorCode.Unauthorized;

    if (isUserBlockedError) {
      setIsUserBlocked(true);
      startBlockTimer();
    }

    showToast({ text: authError.message, variant: "error" });
    retryCountRef.current = 0;
  };

  const authorizeWithPasswordRealm = async () => {
    await clearCredentials({
      errorScenario: "Failure clearing credentials pre-login",
    });
    const { error } = await loginWithPasswordRealm({
      password,
      username: email,
    });

    if (error) {
      await handleAuthError(error);
      return;
    }
    // Reset retry state on successful login
    retryCountRef.current = 0;
    setCanLoggedInUserSeeAppContent(false);

    if (shouldRememberEmail) {
      await saveUnencryptedBooleanValue({
        isTrue: shouldRememberEmail,
        key: UnencryptedStorageKey.IsRememberMeEnabled,
      });
      await saveEmail();
    }
    if (shouldEnableBiometrics) {
      await saveCredentials();
    }
  };

  const handleSignIn = async () => {
    track(LoginAnalyticsEvent.SignInPressed, {
      [AnalyticsFieldName.Source]: LoginAnalyticsPageView.Login,
    });
    setPersistedEmail(email);
    retryCountRef.current = 0;
    await authorizeWithPasswordRealm();
  };

  const handleRememberCredentials = async (isChecked: boolean) => {
    setShouldRememberEmail(isChecked);

    if (!isChecked) {
      await saveUnencryptedBooleanValue({
        isTrue: false,
        key: UnencryptedStorageKey.IsRememberMeEnabled,
      });
    }

    const { RememberMeChecked, RememberMeUnchecked } = LoginAnalyticsEvent;
    track(isChecked ? RememberMeChecked : RememberMeUnchecked, {
      [AnalyticsFieldName.Source]: LoginAnalyticsPageView.Login,
    });
  };

  const setLocalAuthenticationPreferences = async ({
    isChecked,
  }: {
    isChecked: boolean;
  }) => {
    setShouldEnableBiometrics(isChecked);
    void setIsLocalAuthenticationEnabled(isChecked);
  };

  const handleLocalAuthentication = async (isChecked: boolean) => {
    const eventName = isChecked
      ? LoginAnalyticsEvent.BiometricsSetUp
      : LoginAnalyticsEvent.BiometricsDisabled;

    track(eventName, {
      [AnalyticsFieldName.Source]: LoginAnalyticsPageView.Login,
    });

    // Update local state to reflect the user's choice - update the UI and save the preference
    // based on biometric authentication result
    setShouldEnableBiometrics(isChecked);

    if (isChecked) {
      const response = await authenticateAsync({
        // cancelLabel is required for android
        cancelLabel: "Cancel",
        // eslint-disable-next-line @typescript-eslint/naming-convention
        disableDeviceFallback: true,
      });

      void setLocalAuthenticationPreferences({ isChecked: response.success });
    } else {
      void setLocalAuthenticationPreferences({ isChecked: false });
    }
  };

  const shouldShowBiometricsButton = isBiometricAuthAvailable;

  const handlePasswordResetComplete = () => {
    setIsWebviewOpen(false);
    setPassword("");
  };

  return (
    <KeyboardAvoidingView>
      <VStack gap={24}>
        <VStack gap={12}>
          <TextField
            autoComplete="email"
            isBorderless
            isDisabled={isUserBlocked}
            // Added multiline as a workaround to the Passwords toolbar flashing
            // and causing the form to bounce around. More context can be found here:
            // https://teamequip.atlassian.net/browse/JN-926
            isMultiline
            onBlur={() => {
              setPersistedEmail(email);
            }}
            onChange={(value) => setEmail(value.trim())}
            placeholder="Email address"
            testID={joinTestIDs(LoginTestID.Login, LoginTestID.Email)}
            value={email}
          />

          <TextField
            autoComplete="password"
            isBorderless
            isDisabled={isUserBlocked}
            isSecureEntry
            onChange={(value) => setPassword(value)}
            placeholder="Password"
            testID={joinTestIDs(LoginTestID.Login, LoginTestID.Password)}
            value={password}
          />
        </VStack>

        <VStack
          backgroundColor={ColorToken.NeutralBackgroundPrimary}
          borderRadius={12}
          gap={18}
          padding={18}
        >
          <LabelWithSwitch
            isDefaultChecked={shouldRememberEmail}
            label="Remember me"
            onCheckedChange={handleRememberCredentials}
            testID={joinTestIDs(LoginTestID.Login, LoginTestID.RememberMe)}
          />
          {shouldShowBiometricsButton && (
            <LabelWithSwitch
              isDefaultChecked={shouldEnableBiometrics}
              label={getBiometricsLabel({ isFaceRecognitionSupported })}
              onCheckedChange={handleLocalAuthentication}
              testID={joinTestIDs(LoginTestID.Login, LoginTestID.Biometrics)}
            />
          )}
        </VStack>

        <Button
          isDisabled={isSignInDisabled}
          isFullWidth
          isLoading={isLoading}
          leftIcon={FAIconName.ArrowRight}
          onPress={handleSignIn}
          testID="sign-in"
          title="Sign in"
          variant="primary.on_background"
        />
      </VStack>
      {passwordResetUrl && (
        <WebViewModal
          isOpen={isWebviewOpen}
          onClose={() => setIsWebviewOpen(false)}
          onNavigationStateChanged={(navState: WebViewNavigation): void => {
            if (!navState.url.includes("reset")) {
              handlePasswordResetComplete();
            }
          }}
          title="Password expired: Please set new password"
          uri={passwordResetUrl}
        />
      )}
    </KeyboardAvoidingView>
  );
};

export type { LoginFormProps };
export default LoginForm;
